// ============================================================
// api/adminClientConfig.js
// Per-Client Platform Config endpoints driving the Client Detail
// Platform Config tab. All endpoints scoped by clientId in the
// URL — tenancy enforced server-side.
// ============================================================

import { apiGet, apiPatch, apiPost, apiPut } from './client';

// ── Event Type config ────────────────────────────────────────
// Per-eventTypeId PATCH — the bulk Save iterates and PATCHes each
// changed row.
export const getClientEventTypeConfig         = (clientId)                  => apiGet(`/mgmt/admin/clients/${clientId}/event-type-config`);
export const patchClientEventTypeConfigOne    = (clientId, eventTypeId, b)  => apiPatch(`/mgmt/admin/clients/${clientId}/event-type-config/${encodeURIComponent(eventTypeId)}`, b);

// ── Training Type config — same per-id shape ─────────────────
export const getClientTrainingTypeConfig      = (clientId)                  => apiGet(`/mgmt/admin/clients/${clientId}/training-type-config`);
export const patchClientTrainingTypeConfigOne = (clientId, trainingTypeId, b) => apiPatch(`/mgmt/admin/clients/${clientId}/training-type-config/${encodeURIComponent(trainingTypeId)}`, b);

// ── Notification channel config — per-notificationTypeId ─────
export const patchClientNotificationChannelConfigOne = (clientId, notificationTypeId, b) =>
  apiPatch(`/mgmt/admin/clients/${clientId}/notification-channel-config/${encodeURIComponent(notificationTypeId)}`, b);

// ── Survey Cycle config ──────────────────────────────────────
export const getClientSurveyCycleConfig    = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/survey-cycle-config`);
export const patchClientSurveyCycleConfig  = (clientId, body) => apiPatch(`/mgmt/admin/clients/${clientId}/survey-cycle-config`, body);

// ── CarePeople HrModel (2b-15e) — admin-tenant ONLY (D-15e-8) ─
// 'integrated' = manager handles HR themselves (gains the disciplinary safeguarding
// setter); 'separate' = dedicated HR. Set here only — never user-tenant.
export const getClientHrModel   = (clientId)            => apiGet(`/mgmt/admin/clients/${clientId}/carepeople-hr-model`);
export const patchClientHrModel = (clientId, hrModel)   => apiPatch(`/mgmt/admin/clients/${clientId}/carepeople-hr-model`, { hrModel });

// People-17 (2026-10-01): link staff logins to HR profiles. dryRun is required;
// true previews (writes nothing), false applies and is audited.
export const reconcileClientCarePeople = (clientId, dryRun) => apiPost(`/mgmt/admin/clients/${clientId}/carepeople-reconcile`, { dryRun });

// Trial sample data (2026-10-20): preview, add, and remove. Remove needs an
// explicit includeCustomerRecords choice (records the customer added to sample
// people are deleted only when it is true).
export const getClientSampleData    = (clientId) => apiGet(`/mgmt/admin/clients/${clientId}/sample-data`);
export const addClientSampleData    = (clientId) => apiPost(`/mgmt/admin/clients/${clientId}/sample-data`, {});
export const removeClientSampleData = (clientId, includeCustomerRecords) =>
  apiPost(`/mgmt/admin/clients/${clientId}/sample-data/remove`, { includeCustomerRecords });

// Linked clients (2026-10-28): the link group and same-tenant candidates, and
// the whole group in one PUT ({ memberClientIds, mainClientId }; [] leaves it).
export const getClientLinkedClients = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/linked-clients`);
export const putClientLinkedClients = (clientId, body) => apiPut(`/mgmt/admin/clients/${clientId}/linked-clients`, body);

// ── M365 lifecycle config (Platform-03; 2026-08-14) ──────────
export const getClientM365LifecycleConfig   = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/m365-lifecycle-config`);
export const patchClientM365LifecycleConfig = (clientId, body) => apiPatch(`/mgmt/admin/clients/${clientId}/m365-lifecycle-config`, body);

// ── CareRota geofence config (A2Z-admin-only; §4) ─────────────
export const getClientGeofenceConfig   = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/carerota-geofence-config`);
export const patchClientGeofenceConfig = (clientId, body) => apiPatch(`/mgmt/admin/clients/${clientId}/carerota-geofence-config`, body);

// ── Branding (multipart for PUT) ─────────────────────────────
export const getClientBranding  = (clientId) => apiGet(`/mgmt/admin/clients/${clientId}/branding`);
// PUT uses multipart and is implemented directly in the component.
// Including only the path here for clarity.
export const clientBrandingPath = (clientId) => `/mgmt/admin/clients/${clientId}/branding`;

// ── Notification Channel config ──────────────────────────────
export const getClientNotificationChannelConfig    = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/notification-channel-config`);

// ── Audit schedules (read-only here; create/toggle uses careaudit) ──
export const listClientAuditSchedules = (clientId) => apiGet(`/mgmt/admin/clients/${clientId}/audit-schedules`);

// CareAudit endpoints used by the schedules sub-card. These are
// user-tenant routes that admins can hit via their admin token.
// If the auth boundary changes, route an admin-tenant variant.
export const createCareAuditSchedule = (body)                => apiPost('/careaudit/schedules', body);
export const patchCareAuditSchedule  = (scheduleId, body)    => apiPatch(`/careaudit/schedules/${encodeURIComponent(scheduleId)}`, body);

// ── Shared leave calendar (item 24; 2026-08-27) ───────────────
// PUT states the WHOLE record (enabled + mailbox are one decision — the DB
// CHECK makes enabled-without-a-mailbox unrepresentable, so a partial update
// could construct a state the database then rejects).
//
// ⚠ Paths carry NO leading /api — API_BASE_URL already ends /carehub/api, and a
// doubled prefix 404s at the gateway. Six admin surfaces shipped dead that way
// on 2026-08-04 and every one had passing tests, because the page tests mock
// this module and never exercise the URL.
export const getClientSharedCalendar    = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/shared-calendar`);
export const putClientSharedCalendar    = (clientId, body) => apiPut(`/mgmt/admin/clients/${clientId}/shared-calendar`, body);
export const sharedCalendarAction       = (clientId, body) => apiPost(`/mgmt/admin/clients/${clientId}/shared-calendar/actions`, body);

// Business continuity phase 2 (2026-08-28): the emergency-pack SPO push.
// Same whole-record PUT discipline as the shared calendar above.
export const getClientEmergencyPack     = (clientId)       => apiGet(`/mgmt/admin/clients/${clientId}/emergency-pack`);
export const putClientEmergencyPack     = (clientId, body) => apiPut(`/mgmt/admin/clients/${clientId}/emergency-pack`, body);
export const emergencyPackAction        = (clientId, body) => apiPost(`/mgmt/admin/clients/${clientId}/emergency-pack/actions`, body);
